import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';

import { ProphetMetrics, ViewDatesQuery } from '../../../generated/graphql.generated';

type ViewDay = NonNullable<ViewDatesQuery['viewDates']>[0];
import { eventTypeInfoMapV2 } from '../../global.constants';
import { ForecastCardComponent } from '../forecast-card/forecast-card.component';
import { PacingCardComponent } from '../pacing-card/pacing-card.component';

@Component({
  selector: 'app-daily-summary',
  standalone: true,
  imports: [CommonModule, SkeletonModule, TooltipModule, ForecastCardComponent, PacingCardComponent],
  templateUrl: './daily-summary.component.html',
  styleUrls: ['./daily-summary.component.scss'],
})
export class DailySummaryComponent {
  @Input() stats: NonNullable<ViewDatesQuery['viewDates']>[0] | null = null;
  @Input() totalRevenue: number = 0;
  @Input() showForecast = false;
  @Input() showPacing = false;
  @Input() isDayView = false;
  @Input() prophetMetrics: ProphetMetrics | null = null;
  
  // To detect if we should show the revenue block based on parent logic (if not day view, show total revenue anyway)
  @Input() forceShowRevenue = false;
  /** Per-day view-dates rows for the current view — passed through to pacing-card
   *  so it can build the clipboard breakdown. */
  @Input() allDays: ViewDay[] | null = null;

  @Output() filterByType = new EventEmitter<string>();

  get truckUtilization(): number {
    if (!this.stats?.truckCapacity) return 0;
    const used = this.stats.actualTrucksUsed;
    if (used <= 0) return 0;
    return Math.min(100, (used / this.stats.truckCapacity) * 100);
  }

  get crewUtilization(): number {
    if (!this.stats?.crewCapacity) return 0;
    const used = Math.max(this.stats.actualCrewUsed, this.stats.actualCrewRequired || 0);
    if (used <= 0) return 0;
    return Math.min(100, (used / this.stats.crewCapacity) * 100);
  }

  /** The number the crew meter actually fills against (the meter uses the
   *  greater of scheduled crew and required crew — see crewUtilization). */
  get maxCrewUsed(): number {
    return Math.max(this.stats?.actualCrewUsed ?? 0, this.stats?.actualCrewRequired ?? 0);
  }

  /** Uncapped booked percentage for the tooltip (CodeRabbit): the METERS
   *  cap at 100%, but 12 trucks booked against a capacity of 10 must read
   *  as 120% in the tooltip, not 100%. */
  get truckBookedPercent(): number {
    return this.stats?.truckCapacity ? Math.round((this.stats.actualTrucksUsed / this.stats.truckCapacity) * 100) : 0;
  }

  get crewBookedPercent(): number {
    return this.stats?.crewCapacity ? Math.round((this.stats.actualCrewUsed / this.stats.crewCapacity) * 100) : 0;
  }

  /** Crew scheduled but not yet assigned rides its own tooltip row — the
   *  meters fill to it, but counting it as "used" mislabels the tool top
   *  (CodeRabbit). */
  get crewRequiredExtra(): number {
    return Math.max(0, (this.stats?.actualCrewRequired ?? 0) - (this.stats?.actualCrewUsed ?? 0));
  }

  get maxSystemUtilization(): number {
    return Math.max(this.truckUtilization, this.crewUtilization);
  }

  getEventTypeColor(type: string): string {
    const backgroundColor = eventTypeInfoMapV2[type]?.backgroundColor || 'primary-color';
    return `var(--${backgroundColor})`;
  }

  onFilter(type: string) {
    this.filterByType.emit(type);
  }

  get actualMovingJobs(): number {
    if (!this.stats?.totalEventsByType) return 0;
    const movingType = this.stats.totalEventsByType.find(t => t.type.toLowerCase() === 'moving');
    return movingType ? movingType.count : 0;
  }
}
